import { Link, useParams } from "react-router-dom";
import { Check, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import FaqAccordion from "../components/FaqAccordion";
import CtaBand from "../components/CtaBand";
import ArticleCard from "../components/ArticleCard";
import NotFound from "./NotFound";
import { Reveal, Eyebrow, ChapterHeading } from "../components/Reveal";
import {
  SITE,
  getService,
  serviceImage,
  REGION_ORDER,
  locationsByRegion,
} from "../data/site";
import { serviceContent } from "../data/content";
import { articlesForService } from "../data/articles";

export default function ServicePage() {
  const { slug } = useParams();
  const svc = getService(slug);
  if (!svc) return <NotFound />;

  const content = serviceContent(svc);
  const img = serviceImage(svc);
  const articles = articlesForService(svc.slug, 2);

  return (
    <>
      <Seo
        title={`${svc.title} Services England | SIA Licensed | Xtreme Protection England`}
        description={`${svc.tagline} Xtreme Protection delivers ${svc.title.toLowerCase()} across 120+ England towns & cities — SIA licensed, BS 7858 vetted, 24/7. Call ${SITE.phone}.`}
        keywords={svc.keywords.map((k) => `${k}, ${k} England`).join(", ")}
        canonical={`${SITE.domain}/services/${svc.slug}`}
      />

      <section className="tactical-grid border-b border-white/10" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <nav className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber transition-colors">Home</Link>
            <span className="mx-2 text-amber">//</span>
            <Link to="/services" className="hover:text-amber transition-colors">Services</Link>
            <span className="mx-2 text-amber">//</span>
            <span className="text-white">{svc.title}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <Eyebrow>Specialist Service // England-Wide</Eyebrow>
              <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl text-white">
                {svc.title}
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-gray-300 leading-relaxed">
                {svc.tagline}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {svc.keywords.slice(0, 4).map((k) => (
                  <span key={k} className="font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5">
                    {k}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative border border-white/10 p-2 bg-card">
                <div className="absolute -top-1 -left-1 w-5 h-5 border-t-2 border-l-2 border-amber"></div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 border-b-2 border-r-2 border-amber"></div>
                <img
                  src={img.url}
                  alt={`${svc.title} — ${img.alt}`}
                  className="w-full h-64 sm:h-80 object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="service-overview">
        <ChapterHeading number="01" label="THE BRIEF" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal className="space-y-5">
            {content.overview.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-gray-400 leading-relaxed">
                {p}
              </p>
            ))}
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white mb-5">
              What&apos;s included
            </h2>
            <ul className="space-y-3" data-testid="service-includes">
              {svc.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-amber mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <h2 className="font-display font-bold uppercase tracking-wide text-xl text-white mt-8 mb-5">
              Sectors we protect
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="service-sectors">
              {svc.sectors.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest text-gray-400 border border-white/10 px-2.5 py-1.5">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="service-coverage">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="02" label="THE TERRITORY" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-4">
              Where we deliver {svc.short.toLowerCase()}
            </h2>
            <p className="max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed mb-10">
              {svc.title} is available in every region of England. Select your
              town or city for local {svc.short.toLowerCase()} coverage, response
              times and deployment detail.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8" data-testid="service-locations">
            {REGION_ORDER.map((region) => (
              <div key={region}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-amber mb-3 flex items-center gap-2">
                  <MapPin className="w-3 h-3" /> {region}
                </p>
                <p className="text-xs leading-7 text-gray-500">
                  {locationsByRegion(region).map((l, i) => (
                    <span key={l.slug}>
                      <Link
                        to={`/locations/${l.slug}/${svc.slug}`}
                        className="hover:text-amber transition-colors"
                      >
                        {svc.short} {l.name}
                      </Link>
                      {i < locationsByRegion(region).length - 1 && " · "}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-20" data-testid="service-intel">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="03" label="FROM THE INTEL DESK" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight mb-8">
              Reading on {svc.short.toLowerCase()}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} prefix="service-article" />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="service-faq">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="04" label="QUESTIONS" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-10">
              {svc.title} — answered
            </h2>
            <FaqAccordion faqs={content.faqs} testidPrefix="service-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={`Deploy ${svc.short.toLowerCase()} anywhere in England`}
        text={`Free risk assessment, honest recommendations and a written plan. Call ${SITE.phone} or email ${SITE.email} — our command centre answers 24/7.`}
      />
    </>
  );
}
